import type { Contact } from '@catalyst-psa/shared';
import { MAILERLITE_STATUS_LABELS, type MailerliteSubscriberStatus } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';

/**
 * Read-only mirror of the contact's state in MailerLite. PSA never sets this — it is
 * captured when the contact is pushed and refreshed by the MailerLite status sync, so a
 * null status means the contact has not been sent to MailerLite yet.
 */
const TONES: Record<MailerliteSubscriberStatus, 'green' | 'gray' | 'yellow' | 'orange' | 'red'> = {
  active: 'green',
  unconfirmed: 'yellow',
  unsubscribed: 'gray',
  bounced: 'orange',
  junk: 'red',
};

export function MailerliteStatusBadge({
  contact,
  placeholder = '—',
}: {
  contact: Pick<Contact, 'mailerliteStatus' | 'mailerliteStatusAt' | 'mailerliteError'>;
  /** Rendered when MailerLite has never reported a status for this contact. */
  placeholder?: string;
}) {
  const status = contact.mailerliteStatus;
  const checked = contact.mailerliteStatusAt
    ? new Date(contact.mailerliteStatusAt).toLocaleString()
    : null;

  // A failed push is more actionable than a stale status, so it wins the badge.
  if (contact.mailerliteError) {
    return (
      <span title={checked ? `${contact.mailerliteError} (${checked})` : contact.mailerliteError}>
        <Badge tone="red">{contact.mailerliteError}</Badge>
      </span>
    );
  }
  if (!status) return <span className="text-slate-400">{placeholder}</span>;
  return (
    <span title={checked ? `Checked ${checked}` : undefined}>
      <Badge tone={TONES[status]}>{MAILERLITE_STATUS_LABELS[status]}</Badge>
    </span>
  );
}
